<template>
	<div class="custom-dropdown" ref="dropdown">
		<slot name="opener" :handleClick="toggleList">
			<dropdown-custom-opener
				:placeholder="placeholder"
				:selected-label="label"
				:disabled="disabled"
				:is-open="isOpen"
				@handle-click="toggleList"
				@close="closeDD"
				@focus-first="arrowOpenDD"
				ref="opener"
			/>
		</slot>
		<dropdown-custom-position-top :is-open="isOpen" :window-size="windowSize" :opener-dom="refOpener">
			<slot name="list" :open="isOpen" :closeDropDown="closeDD">
				<dropdown-custom-list :list-options="preparedList" :overflow-list="!!parentSelectedKey" key="parent">
					<template v-slot:default>
						<dropdown-custom-list v-if="isMobileView" :child-behavior="true" :list-options="childrenList" />
					</template>
				</dropdown-custom-list>
			</slot>
			<dropdown-custom-position-side
				v-if="childrenList.length && !isMobileView"
				:parent-selected="reactiveInject.parentSelectedOption"
				:window-size="windowSize"
				key="child"
			>
				<dropdown-custom-list :list-options="childrenList" />
			</dropdown-custom-position-side>
		</dropdown-custom-position-top>
	</div>
</template>

<script>
import throttle from 'lodash.throttle';
import DropdownCustomListBtn from '@/components/dropdown-custom/DropdownCustomListBtn';
import DropdownCustomList from '@/components/dropdown-custom/DropdownCustomList';
import DropdownCustomOpener from '@/components/dropdown-custom/DropdownCustomOpener';
import DropdownCustomPositionSide from '@/components/dropdown-custom/DropdownCustomPositionSide';
import DropdownCustomPositionTop from '@/components/dropdown-custom/DropdownCustomPositionTop';
import {initClickOutside, removeClickOutside} from '@/services/click-outside.js';
import {MOBILE_VIEW} from './dd-constants.js';
export default {
	name: 'DropdownCustom',
	components: {
		DropdownCustomPositionTop,
		DropdownCustomPositionSide,
		DropdownCustomOpener,
		DropdownCustomList,
		DropdownCustomListBtn,
	},
	provide() {
		return {
			selectOption: this.selectOption,
			keyController: this.keyController,
			reactiveInject: this.reactiveInject,
		};
	},
	props: {
		value: {
			type: Object,
			default: function () {
				return null;
			},
		},
		listOptions: {
			type: Array,
			default: function () {
				return [];
			},
		},
		placeholder: {type: String, default: ''},
		disabled: {type: Boolean, default: false},
		autoShowSelected: {type: Boolean, default: false},
		opener: {
			type: [Object, HTMLButtonElement],
			default: null,
		},
	},
	created() {
		this.prepareOptionsList(this.listOptions);
	},
	mounted() {
		this.refOpenerDom = this.$refs.opener;
		this.getWindowSize();
		window.addEventListener('resize', this.handleResize);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize);
	},
	data() {
		return {
			isOpen: false,
			windowSize: {
				width: 0,
				height: 0,
			},
			reactiveInject: {
				selectedOption: this.value,
				parentSelectedOption: null,
				autoShowSelected: this.autoShowSelected,
			},
			preparedList: [],
			clickOutsideFn: null,
			throttle: throttle(this.getWindowSize, 66),
			refOpenerDom: null,
		};
	},
	computed: {
		refOpener() {
			return this.opener || this.refOpenerDom;
		},
		isMobileView() {
			return this.windowSize.width <= MOBILE_VIEW;
		},
		childrenList() {
			return this.reactiveInject.parentSelectedOption?.children ?? [];
		},
		parentSelectedKey() {
			return this.reactiveInject.parentSelectedOption?.key ?? false;
		},
		label() {
			return this.value?.value?.enUS ?? '';
		},
	},
	watch: {
		value(newValue) {
			this.reactiveInject.selectedOption = newValue;
			this.closeDD();
		},
		listOptions(newList) {
			this.prepareOptionsList(newList);
		},
	},
	methods: {
		getOpenerDom() {
			return this.opener || this.$refs.opener;
		},
		handleResize() {
			this.throttle();
		},
		getWindowSize() {
			this.windowSize = {
				height: window.innerHeight,
				width: window.innerWidth,
			};
		},
		keyController(e) {
			if (!this.isOpen) return;
			switch (e.key) {
				case 'Enter':
					this.focusFirstChild();
					break;
				case 'ArrowUp':
					e.preventDefault();
					this.focusPrevOption();
					break;
				case 'ArrowDown':
					e.preventDefault();
					this.focusNextOption();
					break;
				case 'Escape':
					e.preventDefault();
					if (this.parentSelectedKey) this.focusParent();
					else {
						this.closeDD();
						this.focusOpener();
					}
					break;
			}
		},
		toggleList() {
			if (this.isOpen) this.closeDD();
			else this.openDD();
		},
		openDD() {
			if (this.isOpen) return;
			this.isOpen = true;
			if (this.autoShowSelected) this.openSelectedOption();
			this.clickOutsideStart();
			this.$emit('dropdown-opened', this.$refs.dropdown)
		},
		arrowOpenDD() {
			this.openDD();
			this.$nextTick(() => {
				this.preparedList[0].focus = true;
			});
		},
		closeDD() {
			if (!this.isOpen) return;
			this.isOpen = false;
			this.clearState();
			this.clickOutsideStop();
		},
		clearState() {
			this.reactiveInject.parentSelectedOption = null;
		},
		openSelectedOption() {
			const parentOpt = this.findParentSelectedOption();
			if (parentOpt?.children?.length) this.selectOption(parentOpt);
		},
		findParentSelectedOption() {
			if (!this.value) return null;
			const selectedKey = this.value.key;
			return this.listOptions.find((par) => {
				if (par.key === selectedKey) return true;
				return par.children.find((opt) => opt.key === selectedKey);
			});
		},
		selectOption(option) {
			if (option.children.length) {
				if (this.parentSelectedKey === option.key) this.reactiveInject.parentSelectedOption = null;
				else this.reactiveInject.parentSelectedOption = option;
			} else {
				this.$emit('input', option);
				this.closeDD();
			}
			if (this.isMobileView)
				this.$nextTick(() => {
					this.scrollDropdownIntoView(option);
				});
		},
		prepareOptionsList(list) {
			this.preparedList = list.map((opt) => {
				return {
					...opt,
					focus: false,
					children: opt.children.map((ch) => ({...ch, focus: false})),
				};
			});
		},
		findFocusedItem() {
			let result = null;
			this.preparedList.forEach((parItem, parIndex) => {
				if (parItem.focus) result = {item: parItem, index: parIndex, parentIndex: -1};
				else
					parItem.children.forEach((item, index) => {
						if (item.focus) result = {item, index, parentIndex: parIndex};
					});
			});
			return result;
		},
		focusParent() {
			const focused = this.findFocusedItem();
			if (!focused || focused.parentIndex < 0) return;
			this.preparedList[focused.parentIndex].focus = true;
			this.$nextTick(() => {
				this.reactiveInject.parentSelectedOption = null;
			});
		},
		focusFirstChild() {
			const focused = this.findFocusedItem();
			if (!focused?.item.children.length || this.parentSelectedKey === focused?.key) return;
			this.selectOption(focused.item);
			this.$nextTick(() => {
				const nextItem = this.preparedList[focused.index].children[0];
				if (!nextItem.disabled) nextItem.focus = true;
			});
		},
		focusPrevOption() {
			const focusedItem = this.findFocusedItem();
			if (!focusedItem) return;
			const {index, parentIndex} = focusedItem;
			if (!index) {
				// for the parent
				if (parentIndex < 0) {
					this.focusOpener();
					return;
				}
			}
			const nextItem =
				parentIndex >= 0 ? this.preparedList[parentIndex].children[index - 1] : this.preparedList[index - 1];
			if (!nextItem.disabled) nextItem.focus = true;
		},
		focusOpener() {
			this.refOpener.focus();
		},
		focusNextOption() {
			const focusedItem = this.findFocusedItem();
			const focusedList = this.reactiveInject.parentSelectedOption?.children || this.preparedList;
			if (!focusedItem || focusedItem.index + 1 === focusedList.length) return;
			const {index, parentIndex} = focusedItem;
			const nextItem =
				parentIndex >= 0 ? this.preparedList[parentIndex].children[index + 1] : this.preparedList[index + 1];
			if (!nextItem.disabled) nextItem.focus = true;
		},
		clickOutsideStart() {
			this.clickOutsideFn = initClickOutside(this.$refs.dropdown, this.closeDD);
		},
		clickOutsideStop() {
			if (!this.clickOutsideFn) return;
			removeClickOutside(this.clickOutsideFn);
			this.clickOutsideFn = null;
		},
		findOptionNode(key) {
			return document.body.querySelector(`[data-js-id='${key}']`);
		},
		scrollDropdownIntoView(focusedItem) {
			if (!focusedItem) return;
			const elLi = this.findOptionNode(focusedItem.key);
			const elScrolled = elLi.parentElement;
			const isVisible = elScrolled.scrollTop <= elLi.offsetTop;
			if (!isVisible) elScrolled.scrollTop = elLi.offsetTop;
		},
	},
};
</script>

<style lang="scss" scoped>
.custom-dropdown {
	position: relative;
}
</style>
